import { useEffect, useState } from "react";
import loginImage01 from "../../assets/images/registration/registration-steps-image-01.jpg";
import loginImage02 from "../../assets/images/registration/registration-steps-image-03.jpg";
import loginImage03 from "../../assets/images/registration/registration-steps-image-04.jpg";
import { useDispatch } from "react-redux";
import { setPartiallyVerified } from "../../Redux/verificationReducer";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { Endpoints } from "../../Global/Endpoints";
import { useSelector } from "react-redux";
import ErrorModal from "../../Components/Security/ErrorModal";

// Mask any email address
export function maskEmail(email) {
  if (!email || !email.includes("@")) return "";
  const [name, domain] = email.split("@");
  const maskedName = name.length <= 2
    ? name[0] + "*".repeat(Math.max(0, name.length - 1))
    : name.slice(0, 2) + "*".repeat(name.length - 2);

  const domainParts = domain.split(".");
  const domainName = domainParts[0];
  const domainMask = domainName.length <= 3
    ? domainName[0] + "*".repeat(Math.max(0, domainName.length - 1))
    : domainName.slice(0, 3) + "*".repeat(domainName.length - 3);

  return `${maskedName}@${domainMask}.${domainParts.slice(1).join(".")}`;
}

// Mask any phone number
export function maskPhone(phone) {
  if (!phone) return "";
  // Remove non-digits for safety, then mask
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return digits;
  return digits.slice(0, 2) + "*".repeat(digits.length - 4) + digits.slice(-2);
}

const AccessCodeForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isSSO = useSelector((state) => state.sso.isSSO); 
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [logo, setLogo] = useState(null);
  const { currentStep = 3, totalSteps = 4 } = location.state || {};
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showEmailPhoneOptions, setShowEmailPhoneOptions] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const dispatch= useDispatch();
  const [accessCode, setAccessCode] = useState(isSSO?ssoData.Promocode:"");
  const [combinedFormData, setCombinedFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const isNewRegistrationFlow = Boolean(location.state?.formData);


  useEffect(() => {

    const loadLogo = async () => {
      if (isHighmarkPortal) {
        const logoModule = await import("./../../assets/images/highmark/ffb-logo.jpg");
        setLogo(logoModule.default);
      } else {
        const logoModule = await import("./../../assets/images/win/logo-win.svg");
        setLogo(logoModule.default);
      }
    };

    loadLogo();
  }, [ssoData]);


const handleEmailClick = () => {
  setShowModal(false);
  navigate('/forgotpassword', { state: { email: maskedEmail } });
};

const handlePhoneClick = async () => {
  try {
    const paramsObject = {
  EmailID: combinedFormData.EmailID,
  Phonenumber: combinedFormData.PhoneNo,
  FirstName: combinedFormData.FirstName,
  LastName: combinedFormData.LastName
};
    // Call your “Forgot Password via Phone” API here
    const result = await axios.post(
  Endpoints.TextForgotPassword,
  null,
  {
    params: { _Params: JSON.stringify(paramsObject) }
  }
);
    setModalMessage("A temporary password has been sent to your registered phone number.");
    setShowEmailPhoneOptions(false);
    setShowModal(true);
  } catch (error) {
    setModalMessage("Failed to send temporary password. Please try again.");
    setShowEmailPhoneOptions(false);
    setShowModal(true);
  }
};

// Get the passed state (combinedFormData) from the previous component
useEffect(() => {
  if (location.state) {
    setCombinedFormData(location.state);
    setAccessCode(location.state.accessCode || location.state.Promocode || "");
    console.log("Received form data:", location.state);
  }
  else {
  }
}, [location.state]);

  const formatDobForApi = (dobValue) => {
    if (!dobValue) return "";
    if (typeof dobValue === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dobValue)) {
      return dobValue;
    }

    const parsedDate = new Date(dobValue);
    return Number.isNaN(parsedDate.getTime())
      ? ""
      : parsedDate.toLocaleDateString("en-US");
  };

  const buildCreateAccountData = () => {
    const formData1 = location.state?.formData || {};

    return {
      FirstName: formData1.firstName || "",
      LastName: formData1.lastName || "",
      PreferredName: formData1.preferredName || "",
      EmailID: formData1.email || "",
      PWD: formData1.password || "",
      ConfirmPassword: formData1.confirmPassword || "",
      AgreeTerms: formData1.agreeTerms || false,
      Country: formData1.country || (isHighmarkPortal ? "United States of America" : ""),
      Language: "",
      Ethnicity: "",
      PhoneNo: "",
      DOB: formatDobForApi(formData1.dateOfBirth),
      TextNotificationStatus: "No",
      Version: "1.0",
      AppLanguage: "English",
      DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
      Company: location.state?.company || "",
      MemberID: "",
      WhyHere: "",
      WINBenefits: "Y",
      Promocode: accessCode || (ssoData.Promocode ? ssoData.Promocode : ""),
      Pronouns: "",
      UpdateFlag: "N",
      JourneyStatus: "",
      HealthPlanId: location.state?.healthPlanID || "",
      RelationshipStatus: "",
      IsHealthProfileCompleted: 0,
      BenefitProvider: location.state?.form3Data || (ssoData.Promocode ? "My employer" : ""),
      SubscriberID: "",
      Gender: "",
      Contract: "",
      SecureCode: "",
      Goal: "",
      CountryCode: "",
    };
  };

  const getAgeFromDOB = (dobString) => {
    if (!dobString) return 0;
    const [month, day, year] = dobString.split("/");
    const dob = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age;
  };

  const navigateToRegistrationFormTwo = (responseData) => {
    navigate("/registration-form-two", {
      state: {
        formData: location.state.formData,
        form2Data: location.state.form2Data,
        form3Data: location.state.form3Data,
        accessCode,
        company: location.state?.company || "",
        healthPlanID: location.state?.healthPlanID || "",
        otherData: { ...location.state?.otherData },
        createAccountResponseData: responseData
      }
    });
  };

  const handleMinorAccessConsent = async (createAccountData, createAccountResponseData) => {
    const minorPayload = {
      Fname: createAccountData.FirstName || "",
      Lname: createAccountData.LastName || "",
      DOB: createAccountData.DOB || "",
      GuardianFname: "",
      GuardianLname: "",
      GuardianDOB: "",
      TelePhn: "",
      Address: "",
      Relation: "",
      Emailid: "",
      MinorValidation: "Y",
      Contract: createAccountResponseData.ContractIdentified || "",
      MemberID: createAccountResponseData.MemberID || "",
    };

    try {
      const response = await axios.post(
        Endpoints.SaveMinorAccessConsent,
        `_Params=${encodeURIComponent(JSON.stringify(minorPayload))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      if (response.data.result === 1) {
        navigate("/minorscreen", {
          state: {
            ...createAccountData,
            Contract: createAccountResponseData.ContractIdentified || "",
            MemberID: createAccountResponseData.MemberID || "",
          }
        });
        return true;
      }

      if (response.data.result === 2) {
        if (!isHighmarkPortal && createAccountData.Country !== "United States of America") {
          const mergedFormData = {
            ...createAccountData,
            BabyCountFlag: createAccountResponseData.BabyCountFlag
          };

          navigate("/patientconsent-form", {
            state: {
              updatedFormData: mergedFormData,
              responseData: createAccountResponseData
            }
          });
          return true;
        }

        navigateToRegistrationFormTwo(createAccountResponseData);
        return true;
      }

      if (response.data.result === 3) {
        setModalMessage("Consent form has already been sent to your parent or guardian. We are still awaiting their response.");
        setShowModal(true);
        return true;
      }

      if (response.data.result === 0) {
        setModalMessage(response.data.resultText || "Unable to fetch minor access consent.");
        setShowModal(true);
        return true;
      }
    } catch (error) {
      setModalMessage("Unable to save minor access consent.");
      setShowModal(true);
      return true;
    }

    return false;
  };

  const handleBack = () => {
    if (isNewRegistrationFlow) {
      navigate("/benefit-options", {
        state: {
          formData: location.state.formData,
          form2Data: location.state.form2Data,
          form3Data: location.state.form3Data,
          accessCode,
          otherData: { ...location.state?.otherData }
        }
      });
      return;
    }
    navigate("/benefit-options", { state: { form4Data: combinedFormData} });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

     // Validate access code
     if (!accessCode.trim()) {
      setErrorMessage("Please enter the access code.");
      return;
    }

    const createAccountData = buildCreateAccountData();
    const paramsObject = {
      ...createAccountData,
      EmployeeID: "",
      EmployeeFirstName: "",
      EmployeeLastName: "",
      RelationshiptoEmployee: ""
    };

    setLoading(true);
    setIsSubmitting(true);

    try {
      setCombinedFormData(createAccountData);
      const response = await axios.post(
        Endpoints.CreateAccount,
        null,
        {
          params: { _Params: JSON.stringify(paramsObject) },
        }
      );

      if (response.data.isWIN === "Y") {
        navigate("/contact-info");
        return;
      }

      if (response.data.IsHealthIDPresent && !response.data.HealthPlanId && isNewRegistrationFlow) {
        navigate("/companyname", {
          state: {
            formData: location.state.formData,
            form2Data: location.state.form2Data,
            form3Data: location.state.form3Data,
            accessCode,
            company: location.state?.company || "",
            healthPlanID: location.state?.healthPlanID || "",
            otherData: { ...location.state?.otherData }
          }
        });
        return;
      }

      if (response.data.Result === 2) {
        const age = getAgeFromDOB(createAccountData.DOB);

        if (!isHighmarkPortal && age < 18) {
          const handledMinorFlow = await handleMinorAccessConsent(createAccountData, response.data);
          if (handledMinorFlow) {
            return;
          }
        }

        navigateToRegistrationFormTwo(response.data);
        return;
      }

      if (response.data.Result === 16) {
        const hiddenEmail = maskEmail(response.data.EmailID);
        const hiddenPhone = maskPhone(response.data.PhoneNumber);
        setMaskedEmail(response.data.EmailID || "your****@domain.com");
        setMaskedPhone(response.data.PhoneNumber || "123****7890");
        setModalMessage(`You have already registered and created an account with email ${hiddenEmail}. Please login with that account. Click on Email if you forgot your password and we will email it to you. If you do not have access to this email and would like a text to ${hiddenPhone}, please click on Phone to get the temporary password to the number.`);
        setShowEmailPhoneOptions(true);
        setShowModal(true);
        return;
      }

      if (response.data.Result === 5) {
        setShowErrorModal(true);
        setErrorModalMessage(response.data.Message);
        return;
      }

      if (response.data.Result === 8) {
        setModalMessage(response.data.Message);
        setShowModal(true);
        return;
      }

      if (response.data.Result === 9) {
        navigate("/contracts", {
          state: {
            updatedFormData: createAccountData,
            apiResponse: response.data
          }
        });
        return;
      }

      if (response.data.Result === 7) {
        setModalMessage(response.data.Message);
        setShowModal(true);
        return;
      }

      if (response.data.Result === 0) {
        navigate("/companyname", {
          state: {
            formData: location.state.formData,
            form2Data: location.state.form2Data,
            form3Data: location.state.form3Data,
            accessCode,
            company: location.state?.company || "",
            healthPlanID: location.state?.healthPlanID || "",
            otherData: { ...location.state?.otherData }
          }
        });
        return;
      }

    } catch (error) {
      console.error("Create account API Error:", error);
      navigate("/companyname", {
        state: {
          formData: location.state.formData,
          form2Data: location.state.form2Data,
          form3Data: location.state.form3Data,
          accessCode,
          company: location.state?.company || "",
          healthPlanID: location.state?.healthPlanID || "",
          otherData: { ...location.state?.otherData }
        }
      });
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    const myCarousel = document.querySelector("#carouselExampleSlidesOnly");
    
    if (myCarousel) {
        new window.bootstrap.Carousel(myCarousel, {
          interval: 5000, // Set interval in milliseconds
        });
      }
  }, []);

  // Handle access code change
  const handleAccessCodeChange = (e) => {
    const input = e.target.value.toUpperCase(); // Convert input to uppercase
    setAccessCode(input);
    setErrorMessage(""); // Clear error message when typing
  };

  // Handle click for 'I don't have an access code'
  const handleNoAccessCodeClick = () => {
    if (isNewRegistrationFlow) {
      navigate("/companyname", {
        state: {
          formData: location.state.formData,
          form2Data: location.state.form2Data,
          form3Data: location.state.form3Data,
          accessCode,
          company: location.state?.company || "",
          healthPlanID: location.state?.healthPlanID || "",
          otherData: { ...location.state?.otherData }
        }
      });
      return;
    }
    navigate("/companyname", { state:{combinedDataMemberId: combinedFormData}}); // Redirect to the new component
  };

   if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>        
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <>
    {/* <div> <LanguageSelector /></div> */}
      {/* Header */}
      <header>
        
        <nav className="navbar navbar-expand-lg container-fixed">
          <a className="navbar-brand" href="#">
          {isHighmarkPortal ? 
            <img src={logo} alt="Logo" className="logo brand-logo" />
          : 
            <img src={logo} alt="Logo" className="logo brand-logo" />
          }            
          </a>
        </nav>
      </header>

      <div className="registration-block-main">
        {/* left carousel start */}
        <div className="registration-image-wrapper">
          <div
            id="carouselExampleSlidesOnly"
            className="carousel slide"
            data-bs-ride="carousel"
          >
            <div className="carousel-inner">
              <div className="carousel-item login-carousel-item active" data-bs-interval="5000">
                <img src={loginImage01} alt="" className="registration-image" />
              </div>
              <div className="carousel-item login-carousel-item" data-bs-interval="5000">
                <img src={loginImage02} alt="" className="registration-image" />
              </div>
              <div className="carousel-item login-carousel-item" data-bs-interval="5000">
                <img src={loginImage03} alt="" className="registration-image" />
              </div>
            </div>
          </div>
        </div>
        {/* left carousel end */}

        {/* registration right form area start */}
        <div className="registration-form-main">
          <div className="registration-form">
            
            <h3 className="page-heading-secondary-black">{t('unlockwinbenefit')}</h3>
            {/* <h3 className="page-heading-bold">Username</h3> */}
            <p>{t('accesscodemsg')} </p>

            <div className="registration-process-form-wrapper mt-5">
              <form onSubmit={handleSubmit}>
                
                <div className="mb-3">
                  <label
                    htmlFor="phoneNumber"
                    className="form-label win-form-label"
                  >
                    {t('accesscode')}
                  </label>
                  <input
                    type="text"
                    className="form-control win-form-control  mobile-view"
                    value={accessCode}
                    onChange={handleAccessCodeChange}
                    required
                    readOnly={isSSO}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                  />
                  {errorMessage && <div className="form-field-error-message">{errorMessage}</div>}
                </div>
                {/* Add clickable sentence */}
                <div className="mt-3">
                  <span
                    onClick={handleNoAccessCodeClick}
                    className="text-primary-color ms-3"
                    style={{cursor: "pointer"}}
                  >
                    {t('dontacesscode')}
                  </span>
                </div>
                
                <div className="mb-3 mt-4">
                {isHighmarkPortal ? (
                      <>
                      <div className="pagination-wrapper mt-5">
                        <button className="previous-btn" onClick={handleBack} type="button"></button>                       
                        <span className="me-3">{currentStep} of {totalSteps}</span>                        
                        <button className="next-btn" onClick={handleSubmit} type="submit" disabled={isSubmitting}></button>
                        </div>
                      </>
                    ) : (
                      <>
                  <button className="win-btn-secondary mobile-view me-3 mb-2 mb-md-0" onClick={handleBack}>{t('back')}</button>
                  <button className="win-btn-primary mobile-view" onClick={handleSubmit} type="submit" disabled={isSubmitting}>
                  {isSubmitting ?  t('nextbutton') :  t('nextbutton')}
                 
                  </button>
                  </>
                    )}
                </div>
              </form>
            </div>

          </div>
        </div>
        {/* registration right form area end */}
      </div> 
      <ErrorModal
        show={showModal}
        message={modalMessage}
        onClose={() => setShowModal(false)}
        showEmailPhoneOptions={showEmailPhoneOptions}
        maskedEmail={maskedEmail}
        maskedPhone={maskedPhone}
        onEmailClick={handleEmailClick}
        onPhoneClick={handlePhoneClick}
        closeButton={true}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorModalMessage}
        isOkay={true}
        onOkay={() => navigate("/eligibility", { state: { combinedDataMemberId: combinedFormData } })}
      />
       
    </>
  );
};
export default AccessCodeForm;
